import type { PartnerDashboardDto } from '@washed/api-client';
export type Dashboard = PartnerDashboardDto;
export const previewData: Dashboard = {
  timeline: Array.from({ length: 14 }, (_, i) => ({
    date: `2026-10-${String(i + 1).padStart(2, '0')}`,
    attributed: [0, 1, 2, 1, 0, 3, 1, 2, 3, 1, 2, 4, 2, 2][i]!,
    paid: [0, 0, 1, 1, 0, 2, 1, 1, 2, 1, 2, 3, 2, 2][i]!,
    retained: [0, 0, 0, 1, 0, 1, 0, 1, 1, 1, 1, 2, 1, 2][i]!,
    earned_xof: String([0, 0, 1, 1, 0, 2, 1, 1, 2, 1, 2, 3, 2, 2][i]! * 2000),
    paid_xof: i === 7 ? '7000' : '0',
  })),
  profile: {
    display_name: 'Ama',
    kind: 'CREATOR',
    status: 'ACTIVE',
    policy_version: 'creator-first-payment-v2',
    bounty_xof: '2000',
    code: 'INF-AMAK7Q2X',
  },
  summary: { attributed: 24, paid: 18, qualified: 16, retained: 11, active: 15 },
  earnings: { pending: '0', held: '0', earned: '36000', paid: '7000' },
  referrals: [
    {
      reference: 'a5d29c317b',
      attributed_at: '2026-10-13T10:00:00Z',
      qualified_at: null,
      status: 'ATTRIBUTED',
      bounty_status: null,
      amount: null,
      paid_amount: '0',
      payable_at: null,
      total: 4,
    },
    {
      reference: '2fc80e71ab',
      attributed_at: '2026-10-09T10:00:00Z',
      qualified_at: '2026-10-12T10:00:00Z',
      status: 'REWARDED',
      bounty_status: 'PAYABLE',
      amount: '2000',
      paid_amount: '0',
      payable_at: null,
      total: 4,
    },
    {
      reference: '48ab2d9e10',
      attributed_at: '2026-09-10T10:00:00Z',
      qualified_at: '2026-09-13T10:00:00Z',
      status: 'REWARDED',
      bounty_status: 'PAYABLE',
      amount: '2000',
      paid_amount: '0',
      payable_at: '2026-10-10T10:00:00Z',
      total: 4,
    },
    {
      reference: 'c06a8f12d3',
      attributed_at: '2026-09-02T10:00:00Z',
      qualified_at: '2026-09-05T10:00:00Z',
      status: 'REWARDED',
      bounty_status: 'PAID',
      amount: '2000',
      paid_amount: '2000',
      payable_at: '2026-10-02T10:00:00Z',
      total: 4,
    },
  ],
  codes: { code: 'INF-AMAK7Q2X', personalisable: true, nextChangeAt: null, previous: [] },
  signingFee: { agreedXof: '10000', paidXof: '10000', paidAt: '2026-09-30T12:00:00Z' },
  payouts: [
    {
      payout_id: 'preview',
      kind: 'COMMISSION',
      provider: 'MIXX',
      reference: 'WASHED-PARTNER-20261008',
      amount_xof: '7000',
      paid_at: '2026-10-08T12:00:00Z',
    },
    {
      payout_id: 'preview-fee',
      kind: 'SIGNING_FEE',
      provider: 'MIXX',
      reference: 'WASHED-PARTNER-20260930',
      amount_xof: '10000',
      paid_at: '2026-09-30T12:00:00Z',
    },
  ],
  page: 1,
  updatedAt: '2026-10-14T16:40:00Z',
};
/* Worst-case data for the break-ui pass: every value is a real shape or the real limit.
   Names: display_name is TEXT 1..120 (migration 0133); a 40-letter single word is a long
   compound first name with no break point. Code: INF- plus 12 letters/digits is the
   longest the server accepts (CREATOR_CODE_MAX_LENGTH). Balance: 12 345 678 payable. */
export const WORST_NAME = 'Akossiwamarieclaireadjoatomegahlangguths';
export const WORST_CODE = 'INF-KWMNPQRST234';
const day = (i: number) => new Date(Date.UTC(2026, 7, 2 + i)).toISOString().slice(0, 10);
function worstCase(d: Dashboard) {
  d.profile.display_name = WORST_NAME;
  d.profile.code = WORST_CODE;
  d.summary = { attributed: 250, paid: 230, qualified: 210, retained: 180, active: 199 };
  d.earnings = { pending: '0', held: '2000', earned: '16345678', paid: '4000000' };
  // 90 days: the first month earns nothing, the middle one a little, the last the most.
  const weight = (i: number) => (i < 30 ? 0 : i < 60 ? 1 + (i % 4) : 6 + ((i * 7) % 9));
  const total = Array.from({ length: 90 }, (_, i) => weight(i)).reduce((a, b) => a + b, 0);
  // Floor each day's share, then hand the few leftover units to the busiest days one each,
  // so the last day is not a spike the real data would not have.
  const series = (target: number) => {
    const shares = Array.from({ length: 90 }, (_, i) => Math.floor((weight(i) / total) * target));
    let left = target - shares.reduce((a, b) => a + b, 0);
    for (let i = 89; left > 0; i = (i + 89) % 90) if (weight(i) > 0) (shares[i]!++, left--);
    return shares;
  };
  const attributed = series(d.summary.attributed),
    paid = series(d.summary.paid),
    retained = series(d.summary.retained),
    earned = series(Number(d.earnings.earned));
  d.timeline = Array.from({ length: 90 }, (_, i) => ({
    date: day(i),
    attributed: attributed[i]!,
    paid: paid[i]!,
    retained: retained[i]!,
    earned_xof: String(earned[i]!),
    paid_xof: i === 70 ? '1500000' : i === 85 ? '2500000' : '0',
  }));
  const statuses = [
    ['ATTRIBUTED', null],
    ['QUALIFIED_PENDING_REVIEW', null],
    ['HELD_FIRST_SERVICE_ISSUE', 'HELD'],
    ['REWARDED', 'PENDING_RETENTION'],
    ['REWARDED', 'PAYABLE'],
    ['REWARDED', 'PAID'],
    ['REJECTED', 'CANCELLED'],
  ] as const;
  d.referrals = Array.from({ length: 25 }, (_, i) => {
    const [status, bounty] = statuses[i % statuses.length]!;
    return {
      reference: (0xa5d29c317b + i * 0x1f3a9).toString(16).padStart(10, '0'),
      attributed_at: `2026-10-${String(30 - (i % 28)).padStart(2, '0')}T10:00:00Z`,
      qualified_at: null,
      status,
      bounty_status: bounty,
      amount: bounty === null || bounty === 'CANCELLED' ? null : i === 4 ? '12345678' : '2000',
      paid_amount: bounty === 'PAID' ? '2000' : '0',
      payable_at: null,
      total: 250,
    };
  });
  d.payouts = [
    {
      payout_id: 'worst-1',
      kind: 'COMMISSION',
      provider: 'MIXX',
      reference: 'WASHEDPARTNERPAYOUT20261008MIXX0000123456',
      amount_xof: '2500000',
      paid_at: '2026-10-23T12:00:00Z',
    },
    {
      payout_id: 'worst-2',
      kind: 'SIGNING_FEE',
      provider: 'MOOV_MONEY',
      reference: 'WASHED-PARTNER-20260921',
      amount_xof: '1500000',
      paid_at: '2026-10-08T12:00:00Z',
    },
  ];
  d.codes = {
    code: WORST_CODE,
    personalisable: true,
    nextChangeAt: null,
    previous: [
      { code: 'INF-KWMNPQRST223', validUntil: '2027-01-10T00:00:00Z', endedBySupervisor: false },
      { code: 'INF-KWMNPQRST225', validUntil: '2027-01-02T00:00:00Z', endedBySupervisor: false },
    ],
  };
}
/* The opposite corner: a one-letter name, no code yet, nothing earned, one chart point. */
function minimalCase(d: Dashboard) {
  d.profile.display_name = 'J';
  d.profile.code = null;
  d.summary = { attributed: 0, paid: 0, qualified: 0, retained: 0, active: 0 };
  d.earnings = { pending: '0', held: '0', earned: '0', paid: '0' };
  d.timeline = [
    { date: '2026-10-30', attributed: 0, paid: 0, retained: 0, earned_xof: '0', paid_xof: '0' },
  ];
  d.referrals = [];
  d.payouts = [];
  d.codes = { code: null, personalisable: true, nextChangeAt: null, previous: [] };
}
export function fixture(mode: string): Dashboard {
  const d = structuredClone(previewData);
  if (mode === 'empty') {
    d.summary = { attributed: 0, paid: 0, qualified: 0, retained: 0, active: 0 };
    d.earnings = { pending: '0', held: '0', earned: '0', paid: '0' };
    d.timeline = d.timeline.map((row) => ({
      ...row,
      attributed: 0,
      paid: 0,
      retained: 0,
      earned_xof: '0',
      paid_xof: '0',
    }));
    d.referrals = [];
    d.payouts = [];
  }
  if (mode === 'worst') worstCase(d);
  if (mode === 'minimal') minimalCase(d);
  return d;
}
/* French groups digits with U+202F (narrow no-break space), which several fonts draw almost
   closed: « 12 345 678 » reads as one string at balance size. A plain no-break space is as
   unbreakable and wide enough to read. */
export const formatXof = (value: string, locale: string): string =>
  new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'XOF',
    maximumFractionDigits: 0,
  })
    .format(BigInt(value))
    .replace(/\u202f/gu, '\u00a0');

/** Dev preview only (?preview&agreement): the accord as GET /v1/partner/agreement sends it. */
const agreementFixture = {
  version: 'accord-createur-2026-10-25',
  title: 'Accord créateur Washed',
  washedSignatory: 'Ryan Tomegah, fondateur',
  articles: [
    {
      title: '1. L’objet.',
      body: 'Le créateur présente Washed à son public et partage son code INF- personnel.',
    },
    {
      title: '2. Les vidéos.',
      body: 'Le créateur publie au moins 2 vidéos pendant la campagne : une au lancement, une vers le 15e jour. Il peut publier davantage, sans cachet en plus.',
    },
    {
      title: '3. Le cachet.',
      body: 'Washed paie 10 000 F d’avance, à la signature, par Mobile Money sur le numéro ci-dessus. Si les 2 vidéos ne sont pas publiées aux dates prévues, il n’y a pas de campagne suivante et le code peut être suspendu. Pour une campagne suivante, le cachet est renouvelé si la campagne précédente a apporté au moins 5 foyers payants. Sinon, la campagne peut continuer avec les commissions seulement.',
    },
    {
      title: '4. La commission.',
      body: '2 000 F par nouveau foyer inscrit avec le code du créateur, une seule fois, dès son premier paiement complet confirmé. Sans limite de nombre. Les renouvellements, les paiements incomplets et les visites en plus ne donnent pas de commission. Si un foyer a déjà donné un autre code, le premier code compte. Washed peut vérifier les inscriptions avant de payer quand beaucoup arrivent en même temps.',
    },
    {
      title: '5. Le paiement.',
      body: 'À la fin de chaque campagne, après 7 jours de vérification des paiements, par virement Mobile Money sur le numéro ci-dessus. Les foyers inscrits après la fin d’une campagne sont payés à la fin de la campagne suivante. Le créateur suit ses foyers, ses gains et ses paiements sur partenaires.washedafrica.com.',
    },
    {
      title: '6. Ce que dit le créateur.',
      body: 'Il utilise seulement les informations et les prix des documents Washed. Il ne dit jamais « gratuit », « illimité » ou « à volonté », ne promet pas de remboursement et ne se présente pas comme membre de l’équipe Washed. Il indique que c’est un partenariat. Il ne filme personne sans son accord.',
    },
    {
      title: '7. La suspension.',
      body: 'En cas de fausses inscriptions, de promesses fausses ou de non-respect de l’article 6, Washed peut suspendre le code tout de suite. Les commissions déjà gagnées sur de vrais foyers restent dues.',
    },
    {
      title: '8. La fin.',
      body: 'Chacun peut arrêter à la fin d’une campagne. Les sommes gagnées sont payées.',
    },
  ],
  providers: { MIXX: 'Mixx by Yas', FLOOZ: 'Flooz' },
  displayName: 'Ama',
  signed: null,
};
export async function previewAgreement<R>(path: string, body?: object): Promise<R> {
  await new Promise((r) => setTimeout(r, 300));
  if (!body) return agreementFixture as R;
  const b = body as { signerName: string; mobileMoneyNumber: string };
  const signedAt = new Date().toISOString();
  return {
    signed: {
      version: agreementFixture.version,
      signerName: b.signerName.trim(),
      signedAt,
      campaignStart: signedAt.slice(0, 10),
      campaignEnd: signedAt.slice(0, 10),
      sha256: '0'.repeat(64),
      text: `${agreementFixture.title}\n\nCréateur : ${b.signerName}\nNuméro Mobile Money : +228 ${b.mobileMoneyNumber}\n\n(aperçu)`,
    },
  } as R;
}
